import { basisPoints, type BasisPoints } from './rate.js'

/**
 * Money, as a whole number of cents.
 *
 * Never a plain `number` in a domain signature. Two reasons, and the second is the
 * one that bites: floating point cannot represent 0.1, so repeated percentage
 * arithmetic drifts; and a bare `number` parameter accepts a percentage, a count
 * or a ward class just as happily as an amount.
 */
declare const centsBrand: unique symbol
export type Cents = number & { readonly [centsBrand]: true }

/**
 * Parse an AMOUNT from a raw number.
 *
 * ⚠ The conditional type is the same control `serviceDate()` carries, for the
 * same reason: a {@link CentsDelta} is still assignable to `number`, so a plain
 * `(value: number)` signature would accept one without a cast. `cents(delta)`
 * would then compile, succeed for every UPWARD adjustment (a positive delta is
 * a valid amount) and throw only on a DOWNWARD one — a defect that passes every
 * test written with a positive fixture and fails in production on the first
 * overbilling correction. A brand that only stops assignment, never
 * construction, is the laundering route `service-date.ts` records surviving
 * eight negative tests.
 */
export function cents<T extends number>(value: T & (T extends CentsDelta ? never : T)): Cents {
  // Widened to `number` before anything else: the parameter's type is the
  // laundering GUARD, and a guard is not the type the body reasons about.
  const raw: number = value
  if (!Number.isSafeInteger(raw)) {
    throw new RangeError(`amount must be safe whole cents, received ${raw}`)
  }
  if (raw < 0 || Object.is(raw, -0)) {
    throw new RangeError(`amount must not be negative, received ${raw}`)
  }
  return raw as Cents
}

/**
 * A SIGNED movement of money, in whole cents. **A different brand from
 * {@link Cents}, not an alias of it.**
 *
 * ## Why `Cents` cannot express this
 *
 * `cents()` throws on any negative and on `-0`, deliberately — an amount is a
 * quantity of money owed and a negative one is a defect. But Annex A stories 3
 * and 4 put a DOWNWARD adjustment on the intake path as an ordinary, expected
 * record: an HSP overbilled, and the correction is negative. Reusing `Cents`
 * for it means every downward adjustment throws at the boundary, which is
 * discovered on the first real correction rather than at compile time.
 *
 * ## Why a brand and not a bare `number`
 *
 * The failure this closes is passing an amount where a movement belongs, or the
 * reverse — `applyDelta(bill.amount, bill.amount)` typechecks against two bare
 * `number`s and doubles the bill. Distinct brands make both directions a
 * compile error, and {@link cents} and {@link centsDelta} each refuse the
 * other's values at the constructor too.
 *
 * ## What the TYPE does not say, stated rather than implied
 *
 * ⚠ The brand carries no non-zero guarantee, exactly as `Cents` carries no
 * non-negative guarantee — both are constructor properties. {@link centsDelta}
 * refuses `0` because an adjustment of nothing is a malformed record. A DERIVED
 * component movement (the subsidy or co-payment half of a delta, from
 * {@link applyDeltaToSplit}) legitimately IS zero whenever the rate is `0` or
 * `10000`, and it is produced only by that function, never parsed from input.
 */
declare const centsDeltaBrand: unique symbol
export type CentsDelta = number & { readonly [centsDeltaBrand]: true }

/**
 * Parse a signed movement from a raw number — the record's own delta.
 *
 * Refuses `0`: an adjustment record that moves nothing is malformed, not a
 * no-op. Treating it as a no-op would accept a record, record no effect, and
 * report success — the shape of "an outcome exists with no effect" that D4 of
 * the file-exchange plan exists to make impossible.
 *
 * ⚠ Refuses a {@link Cents} at the constructor for the reason {@link cents}
 * gives in the other direction.
 */
export function centsDelta<T extends number>(value: T & (T extends Cents ? never : T)): CentsDelta {
  const raw: number = value
  if (!Number.isSafeInteger(raw)) {
    throw new RangeError(`a movement must be safe whole cents, received ${raw}`)
  }
  // `=== 0` catches `-0` too (`-0 === 0`), which is the one spelling that would
  // otherwise slip past a `value === 0` written as `Object.is(value, 0)`.
  if (raw === 0) {
    throw new RangeError(
      `a movement of 0 cents is a malformed record, not a no-op: an adjustment that changes ` +
        `nothing has no effect to record, and accepting it would report success for a record ` +
        `that did nothing`,
    )
  }
  return raw as CentsDelta
}

export function addCents(...values: Cents[]): Cents {
  return values.reduce((total, value) => total + value, 0) as Cents
}

/**
 * Split an amount into a subsidised part and a co-payment, by basis points.
 *
 * **Basis points, not a percentage, are the canonical unit.** One basis point is
 * 1/100 of a percent, so 80% is 8000. The arithmetic is done in `BigInt` because
 * the obvious `Math.floor((total * percentage) / 100)` puts a float in the middle
 * of a money calculation: at a total of 11000 and 0.7 percent it yields
 * 7699.999999999999 and floors to 76 cents where the exact answer is 77. The sum
 * still reconciles — the co-payment is the remainder — so the error hides in the
 * split rather than the total, and no conservation test can see it.
 *
 * **The co-payment is the remainder, never a second rounding.** Computing both
 * sides independently loses or invents a cent whenever the split does not divide
 * evenly, and at 4,879 bills a day a one-cent leak is about $17,800 a year that
 * reconciles against nothing.
 *
 * Rounding direction is a policy question per scheme, not a default to bury here.
 * Until a scheme rule states otherwise this floors the subsidy, which resolves the
 * residual cent to the officer's co-payment. `TODO(SCHEME-ROUNDING)` — carry the
 * direction on the rule once the scheme policies are furnished.
 */
export function splitByBasisPoints(
  total: Cents,
  rate: BasisPoints,
): { subsidy: Cents; coPayment: Cents } {
  if (!Number.isSafeInteger(rate) || rate < 0 || rate > 10_000) {
    throw new RangeError(`basis points must be a whole number between 0 and 10000, received ${rate}`)
  }
  const subsidy = Number((BigInt(total) * BigInt(rate)) / 10_000n)
  return {
    subsidy: subsidy as Cents,
    coPayment: (total - subsidy) as Cents,
  }
}

/**
 * Split by a percentage, which must be expressible exactly in basis points.
 *
 * A percentage carrying more than two decimal places is refused rather than
 * silently rounded: a scheme rate we cannot represent exactly is a question for
 * the Authority, not something to approximate inside a subsidy calculation.
 */
export function splitByPercentage(
  total: Cents,
  percentage: number,
): { subsidy: Cents; coPayment: Cents } {
  if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100) {
    throw new RangeError(`percentage must be between 0 and 100, received ${percentage}`)
  }
  const rounded = Math.round(percentage * 100)
  if (Math.abs(percentage * 100 - rounded) > 1e-9) {
    throw new RangeError(
      `percentage ${percentage} is not expressible in whole basis points; ` +
        `use splitByBasisPoints if a finer rate is genuinely required`,
    )
  }
  return splitByBasisPoints(total, basisPoints(rounded))
}

/**
 * Split a DIFFERENT total at the ratio an EXISTING split already represents —
 * never by re-deriving a basis-points rate from scratch.
 *
 * ## ★ C3 (final review, PROVEN) — why this exists at all
 *
 * `services/proration`'s three subsidy caps
 * (`applyChServiceSubsidyEligibility`, `applyChildSubsidisedLengthCap`,
 * `applyPeggedWardRateCap`) chain: `services/gateway/src/routes/bills.ts`
 * threads one cap's output into the next. Before this function existed, the
 * pegged ward rate cap split its benchmark with `splitByBasisPoints(benchmark,
 * result.appliedRule.rate)` — the ORIGINAL, immutable flat rate — regardless
 * of what an earlier cap had already done to `result`. Proven live: a child
 * ward B2 stay of 240 days against a seeded 120-day cap correctly reduces a
 * flat 80%/$800.00 split to `{ subsidy: 40000, coPayment: 60000 }` (exactly
 * half, the day-count ratio); the pegged cap then re-derived its benchmark
 * split from the flat 8000bp instead of this bill's now-40% ratio, and
 * answered `{ subsidy: 90400, coPayment: 9600 }` — **MORE subsidy than the
 * FLAT split itself**, with the child cap silently discarded.
 *
 * `of` is deliberately not `Split` (no `amount` field required): a
 * `ProrationResult` mid-chain has no separate `amount` of its own — `subsidy +
 * coPayment` of the split so far IS the bill's amount, by `prorate()`'s own
 * invariant, and re-deriving it from a field this function does not need
 * would be a second, driftable copy of the same fact.
 *
 * `amount === 0` returns `{ subsidy: 0, coPayment: total }` rather than
 * dividing by zero: a split with nothing subsidised has no ratio to scale, and
 * zero is the only answer that keeps every later cap composing correctly on
 * top of it (0% of anything is still 0%).
 */
export function splitAtRatioOf(
  total: Cents,
  of: { readonly subsidy: Cents; readonly coPayment: Cents },
): { subsidy: Cents; coPayment: Cents } {
  const amount = (of.subsidy + of.coPayment) as Cents
  if (amount === 0) {
    return { subsidy: 0 as Cents, coPayment: total }
  }
  const subsidy = Number((BigInt(total) * BigInt(of.subsidy)) / BigInt(amount))
  return {
    subsidy: subsidy as Cents,
    coPayment: (total - subsidy) as Cents,
  }
}

/** An amount and the two halves it was split into. Always `subsidy + coPayment === amount`. */
export interface Split {
  readonly amount: Cents
  readonly subsidy: Cents
  readonly coPayment: Cents
}

/**
 * ★ THE FLOOR, expressed exactly once.
 *
 * `amount + delta` must not go below zero. A negative bill is a Healthcare
 * Service Provider owing the government money, which is a refund flow — nothing
 * in Annex A describes one, and inventing it inside an adjustment would move
 * money along a path nobody designed.
 *
 * Every public surface delegates to it: {@link applyDelta} throws (it is the
 * primitive, used where the caller has already established the floor holds),
 * {@link applyDeltaToSplit} returns a refusal the intake path can turn into
 * `ADJUSTMENT_BELOW_ZERO`, and `services/proration`'s `reapplyEpisodeCaps`
 * returns the same refusal for the capped-bill path (S-034). Two spellings of
 * one rule is how one of them ends up wrong — the argument S-011 records for
 * the scope predicate.
 *
 * ⚠ EXPORTED 2026-08-16, having been module-private until then. The third
 * caller is in another package and the only alternatives were a second copy of
 * the floor rule or a fabricated round-trip through a split it does not want.
 *
 * `BigInt` in the middle, `Number` only at the boundary, exactly as
 * {@link splitByBasisPoints} already does.
 */
export function resultingAmount(amount: Cents, delta: CentsDelta): { readonly below: true } | { readonly below: false; readonly amount: Cents } {
  const resulting = Number(BigInt(amount) + BigInt(delta))
  if (!Number.isSafeInteger(resulting)) {
    throw new RangeError(
      `${amount} + ${delta} is ${resulting}, which is not a safe integer. Both operands are ` +
        `validated whole cents, so this is a defect in this function, not in its inputs.`,
    )
  }
  if (resulting < 0) return { below: true }
  return { below: false, amount: resulting as Cents }
}

/**
 * Apply a signed movement to an amount.
 *
 * Throws when the result would be negative — see {@link resultingAmount}. The
 * intake path calls {@link applyDeltaToSplit} instead, which reports the same
 * refusal as a value rather than an exception, because there it is an ordinary
 * per-record outcome a person has to be told about.
 */
export function applyDelta(amount: Cents, delta: CentsDelta): Cents {
  const result = resultingAmount(amount, delta)
  if (result.below) {
    throw new RangeError(
      `applying ${delta} to ${amount} would take the running total below zero. A bill cannot be ` +
        `negative: that would be the provider owing the government money, which is a refund and ` +
        `not an adjustment.`,
    )
  }
  return result.amount
}

/**
 * What {@link applyDeltaToSplit} answers. `ok: false` is a refusal, reported as
 * a VALUE rather than thrown — an ordinary per-record outcome the intake path
 * has to tell a person about, the same discipline `BELOW_ZERO` already
 * follows.
 *
 * ⚠ `RATE_MISMATCH` (C2, final review) is NOT the same failure as `BELOW_ZERO`.
 * `BELOW_ZERO` says the MOVEMENT is too large for this bill. `RATE_MISMATCH`
 * says the bill's OWN preceding split was never the flat rate's split to begin
 * with — a subsidy cap (community-hospital ineligibility, the child length-of-
 * stay cap, the pegged ward rate excess) rewrote it after `prorate()` ran, and
 * nothing recorded that the flat rate no longer reproduces it. See
 * {@link applyDeltaToSplit}'s own docstring for the reproduction this closes.
 */
export type DeltaApplication =
  | {
      readonly ok: true
      /** The split of the NEW total — computed once, from the total. */
      readonly resulting: Split
      /** `resulting.subsidy − preceding.subsidy`. May be zero; see {@link CentsDelta}. */
      readonly deltaSubsidy: CentsDelta
      /** `resulting.coPayment − preceding.coPayment`. May be zero. */
      readonly deltaCoPayment: CentsDelta
    }
  | { readonly ok: false; readonly reason: 'BELOW_ZERO' }
  | { readonly ok: false; readonly reason: 'RATE_MISMATCH'; readonly flatSplit: Split }

/**
 * ★ Apply a signed movement to an already-split bill, and derive the movement
 * of each half.
 *
 * ```
 * resultingAmount  = preceding.amount + delta                  (must be >= 0)
 * resultingSplit   = splitByBasisPoints(resultingAmount, rate)  (the SAME function)
 * deltaSubsidy     = resultingSplit.subsidy   - preceding.subsidy
 * deltaCoPayment   = resultingSplit.coPayment - preceding.coPayment
 * ```
 *
 * ## ⚠ Why the delta is NOT split
 *
 * The obvious implementation is `splitByBasisPoints(delta, rate)` and adding
 * the two halves onto the stored split. It is wrong, it reconciles perfectly,
 * and no conservation test that only checks `deltaSubsidy + deltaCoPayment ===
 * delta` can see it.
 *
 * `splitByBasisPoints` FLOORS the subsidy, so `split(a) + split(b)` is not
 * `split(a + b)`: at 75% a bill of 11000 splits 8250/2750, and a delta of +7
 * splits 5/2 — giving 8255/2752 for a total of 11007, whose actual split is
 * 8255/2752... and at other residues it differs by a cent. Each independently
 * rounded delta can migrate a cent from the public purse to the officer, or
 * back, and the total still adds up. `money.ts` already prices exactly this
 * mistake one function up: at 4,879 bills a day, one cent is about $17,800 a
 * year that reconciles against nothing.
 *
 * So the running split is always the split of the running TOTAL, computed once.
 * The consequence is a property worth stating: the residual cent depends only
 * on the final total, never on the order or the count of the adjustments that
 * produced it.
 *
 * @param preceding The split as it stands. Its own consistency is asserted, not assumed.
 * @param rate The rate the ORIGINAL was computed under. Re-selecting a rate is
 *   a decision this function must never take — see the file-exchange plan's D2,
 *   which is a recorded clarification (Q-27), not a settled fact.
 *
 * ## ⚠ C2 (final review, PROVEN BY EXECUTION) — the guard this function is now
 * built around
 *
 * `preceding` is asserted to equal `splitByBasisPoints(preceding.amount, rate)`
 * — the split the FLAT rate would itself produce — alongside the reconcile
 * check above. Without it, this function silently RE-DERIVES the resulting
 * split from `rate` and the new total, discarding whatever cap a downstream
 * rule (`applyChServiceSubsidyEligibility`, `applyChildSubsidisedLengthCap`,
 * `applyPeggedWardRateCap`) had already written onto the stored split. Proven
 * live: a $1,000.00 bill at 80% ruled CH-ineligible (subsidy forced to 0, the
 * community-hospital code CHPA) stores `{ subsidy: 0, coPayment: 100000 }`; an
 * ordinary +$1.00 adjustment recomputed `splitByBasisPoints(100100, 8000)` and
 * answered `{ subsidy: 80080, coPayment: 20020 }` — **a $1.00 correction moved
 * $800.80 from the officer to the public purse**, with `deltaSubsidy=80080`,
 * `deltaCoPayment=-79980`, conservation holding throughout and every existing
 * guard (the DB CHECK, this function's own reconcile assertion,
 * `resolutionDrift`, which compares only rule id/version/rate) silent, because
 * every one of them was built to catch a DIFFERENT failure. A capped bill now
 * refuses `RATE_MISMATCH` instead — loudly, and BEFORE any money moves — rather
 * than silently reverting the cap. Flat-rate bills and ward-scheme bills (both
 * genuinely ARE `splitByBasisPoints` of their own rate) pass unchanged, and so
 * does every CHAINED adjustment on either, because `resulting` is always
 * produced by this same function.
 *
 * ★ **RE-APPLICATION EXISTS AS OF 2026-08-16, and it is NOT here — deliberately.**
 * The review that found C2 asked that the cap be "persisted so it can be
 * re-applied rather than lost" once the guard refuses it. The guard is the
 * load-bearing half and is what this function does; the other half is
 * `reapplyEpisodeCaps` in `services/proration/src/episode-caps.ts`, which
 * RE-RUNS Annex A 41/44/46/47 against the corrected total for a caller that can
 * see the episode facts and the reference series — never a derived "effective
 * rate", because the CH cap is exactly 0bp but a length-of-stay cap and a
 * pegged-rate cap are not flat-rate fractions of the amount at all, so one
 * persisted number would silently approximate two controls it cannot represent.
 *
 * ⚠ **This function is unchanged by that, and must stay unchanged.** It is the
 * answer for every caller that CANNOT see the episode — it holds no reference
 * data, reaches for no database and takes no clock, and a version of it that
 * tried to re-apply a cap from the rate alone is precisely the defect C2 is.
 * `RATE_MISMATCH` remains the correct answer here; what changed is what the
 * adjustment grammar may do when it receives one.
 */
export function applyDeltaToSplit(preceding: Split, delta: CentsDelta, rate: BasisPoints): DeltaApplication {
  if (preceding.subsidy + preceding.coPayment !== preceding.amount) {
    // A wiring defect, never a refusal a counterparty can act on — the same
    // classification `prorate()`'s officer-mismatch throw carries. It means the
    // caller assembled a split from two different bills.
    throw new Error(
      `the preceding split does not reconcile: ${preceding.subsidy} + ${preceding.coPayment} is not ` +
        `${preceding.amount}. This is a wiring defect in the caller, not a refusable record.`,
    )
  }
  // C2's guard. `preceding.amount` is safe to re-split here regardless of what
  // `delta` turns out to be: this is a pure re-derivation of what the FLAT rate
  // would have produced for the amount as it stands today, not a write.
  const flat = splitByBasisPoints(preceding.amount, rate)
  if (flat.subsidy !== preceding.subsidy || flat.coPayment !== preceding.coPayment) {
    return { ok: false, reason: 'RATE_MISMATCH', flatSplit: { amount: preceding.amount, ...flat } }
  }
  const total = resultingAmount(preceding.amount, delta)
  if (total.below) return { ok: false, reason: 'BELOW_ZERO' }

  const split = splitByBasisPoints(total.amount, rate)
  return {
    ok: true,
    resulting: { amount: total.amount, subsidy: split.subsidy, coPayment: split.coPayment },
    // Derived, never parsed: zero is a legitimate answer here (a rate of 0 moves
    // only the co-payment; a rate of 10000 moves only the subsidy), which is why
    // these do not go through `centsDelta()`.
    deltaSubsidy: (split.subsidy - preceding.subsidy) as CentsDelta,
    deltaCoPayment: (split.coPayment - preceding.coPayment) as CentsDelta,
  }
}
